import { Link } from "react-router-dom";
import { ChevronRight, Copy, Download, Globe, Mail, MapPin, MessageCircle, Pencil, Phone, Plus, Smartphone, Star, StickyNote, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { Avatar } from "@/components/ContactRow";
import ReminderSection from "@/components/ReminderSection";
import { fmtDate, joinAddress } from "@/lib/toto";

const Quick = ({ icon: Icon, label, href, testid, onOpen }) => (
  <a href={href || undefined} target={href?.startsWith("http") ? "_blank" : undefined} rel="noreferrer" onClick={(e) => (!href ? (e.preventDefault(), toast(`No ${label.toLowerCase()} available.`)) : onOpen && (e.preventDefault(), onOpen()))}
    className={`inline-flex items-center gap-2 h-11 px-4 rounded-full text-[14px] font-semibold transition-colors ${href ? "bg-[#E5E5FD] text-[#0000EC] hover:bg-[#d8d8fc]" : "bg-[#F1F5F9] text-[#94A3B8] cursor-not-allowed"}`} data-testid={testid}>
    <Icon className="w-4 h-4" strokeWidth={2.3} />{label}
  </a>
);

const Item = ({ label, value, href, wide }) => {
  if (!value) return null;
  const copy = (e) => { e.preventDefault(); navigator.clipboard?.writeText(value); toast.success(`${label} copied`); };
  return (
    <div className={`group rounded-[14px] bg-[#F8FAFC] px-4 py-3 ${wide ? "col-span-2" : ""}`}>
      <p className="text-[12px] font-semibold uppercase tracking-wide text-[#64748B]">{label}</p>
      <div className="flex items-center gap-2 mt-0.5">
        {href ? <a href={href} target={href.startsWith("http") ? "_blank" : undefined} rel="noreferrer" className="flex-1 min-w-0 truncate text-[15px] font-semibold text-[#0F172A] hover:text-[#0000EC]">{value}</a>
          : <p className="flex-1 min-w-0 truncate text-[15px] font-semibold">{value}</p>}
        <button onClick={copy} className="opacity-0 group-hover:opacity-100 transition-opacity text-[#64748B] hover:text-[#0000EC]" title="Copy"><Copy className="w-4 h-4" /></button>
      </div>
    </div>
  );
};

const Card = ({ title, action, children, testid }) => (
  <section className="t-card p-5" data-testid={testid}>
    <div className="flex items-center justify-between mb-4"><h2 className="text-[17px] font-bold">{title}</h2>{action}</div>
    {children}
  </section>
);

export default function ContactDetailDesktop({ c, id, catName, front, back, events, wa, site, tel, onFav, onDelete, saveVcf, google, setPreview, openPicker, removeEvent, onWhatsApp }) {
  const address = [joinAddress(c), c.city, c.state, c.postalCode, c.country].filter(Boolean).join(", ");
  const emails = (c.email || "").split(/[,;\s]+/).filter(Boolean);
  return (
    <div className="pt-6" data-testid="contact-detail-desktop">
      <div className="flex items-center justify-between">
        <nav className="flex items-center gap-1.5 text-[14px] text-[#64748B]">
          <Link to="/contacts" className="hover:text-[#0000EC] font-medium" data-testid="breadcrumb-contacts">Contacts</Link>
          <ChevronRight className="w-4 h-4" /><span className="font-semibold text-[#0F172A] truncate max-w-[320px]">{c.name || c.companyName}</span>
        </nav>
        <div className="flex items-center gap-2">
          <button onClick={onFav} className={`h-10 px-4 rounded-full border text-[14px] font-semibold inline-flex items-center gap-2 transition-colors ${c.isFavorite ? "bg-[#FEF9C3] border-[#FDE68A] text-[#B45309]" : "border-[#E2E8F0] hover:bg-[#F8FAFC]"}`} data-testid="contact-favorite">
            <Star className={`w-4 h-4 ${c.isFavorite ? "fill-[#D97706] text-[#D97706]" : ""}`} />{c.isFavorite ? "Favorited" : "Favorite"}
          </button>
          <Link to={`/contacts/${id}/edit`} className="h-10 px-4 rounded-full border border-[#E2E8F0] text-[14px] font-semibold inline-flex items-center gap-2 hover:bg-[#F8FAFC]" data-testid="contact-edit"><Pencil className="w-4 h-4" />Edit</Link>
          <button onClick={onDelete} className="h-10 px-4 rounded-full border border-[#FECACA] text-[#DC2626] text-[14px] font-semibold inline-flex items-center gap-2 hover:bg-[#FEF2F2]" data-testid="contact-delete"><Trash2 className="w-4 h-4" />Delete</button>
        </div>
      </div>

      <div className="t-card mt-5 p-6 flex items-center gap-6 relative overflow-hidden">
        <div className="absolute inset-y-0 left-0 w-1.5 bg-[#0000EC]" />
        <Avatar name={c.name || c.companyName} size={84} />
        <div className="flex-1 min-w-0">
          <h1 className="text-[28px] font-extrabold leading-tight truncate" data-testid="contact-name">{c.name || c.companyName}</h1>
          <p className="text-[16px] text-[#475569] mt-0.5 truncate">{[c.designation, c.companyName].filter(Boolean).join(" · ")}</p>
          <div className="flex flex-wrap items-center gap-2 mt-3">
            <span className="rounded-full bg-[#E5E5FD] text-[#0000EC] px-3 py-1 text-[12px] font-bold">{catName}</span>
            {c.city && <span className="inline-flex items-center gap-1 text-[13px] text-[#64748B]"><MapPin className="w-3.5 h-3.5" />{c.city}</span>}
            <span className="text-[13px] text-[#94A3B8]">· Added {fmtDate(c.createdAt)}</span>
          </div>
        </div>
        <div className="flex flex-col items-end gap-3">
          <div className="flex gap-2">
            <Quick icon={Phone} label="Call" href={tel} testid="action-call" />
            <Quick icon={MessageCircle} label="WhatsApp" href={wa && `https://wa.me/${wa}`} onOpen={onWhatsApp} testid="action-whatsapp" />
            <Quick icon={Mail} label="Email" href={emails.length ? `mailto:${emails.join(",")}` : null} testid="action-email" />
            <Quick icon={Globe} label="Website" href={site} testid="action-website" />
          </div>
          <div className="flex gap-2">
            <button onClick={saveVcf} className="t-btn t-btn-primary h-10 px-4 text-[14px]" data-testid="save-vcard"><Download className="w-4 h-4" />Download vCard</button>
            <button onClick={google} className="t-btn t-btn-outline h-10 px-4 text-[14px]" data-testid="save-google-contacts"><Smartphone className="w-4 h-4" />Add to Google Contacts</button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-6 mt-6">
        <div className="col-span-2 space-y-6">
          <Card title="Contact details" testid="contact-business-info">
            <div className="grid grid-cols-2 gap-3">
              <Item label="Mobile" value={c.phone} href={tel} />
              <Item label="Alternate" value={c.alternatePhone} href={c.alternatePhone && `tel:${c.alternatePhone.replace(/[^\d+]/g, "")}`} />
              <Item label="WhatsApp" value={c.whatsapp && c.whatsapp !== c.phone ? c.whatsapp : null} href={wa && `https://wa.me/${wa}`} />
              {emails.map((e, i) => <Item key={e} label={i ? "Email (2)" : "Email"} value={e} href={`mailto:${e}`} />)}
              <Item label="Website" value={c.website} href={site} />
              <Item label="Company" value={c.companyName} />
              <Item label="Designation" value={c.designation} />
            </div>
          </Card>
          <Card title="Address" testid="contact-address">
            {address ? (
              <div className="flex gap-3"><span className="w-10 h-10 rounded-[12px] bg-[#FFF7ED] flex items-center justify-center shrink-0"><MapPin className="w-5 h-5 text-[#EA580C]" /></span><p className="text-[15px] leading-relaxed">{address}</p></div>
            ) : <p className="text-[14px] text-[#64748B]">No address saved.</p>}
          </Card>
          <Card title="Events" testid="contact-events" action={<button className="t-link inline-flex items-center gap-1" onClick={openPicker} data-testid="contact-add-event"><Plus className="w-4 h-4" />Add to Event</button>}>
            {events.length ? (
              <div className="flex flex-wrap gap-2">
                {events.map((e) => (
                  <span key={e.id} className="inline-flex items-center gap-1.5 pl-3 pr-1 h-9 rounded-full bg-[#E5E5FD] text-[#0000EC] text-[14px] font-semibold" data-testid={`contact-event-${e.id}`}>
                    <Link to={`/events/${e.id}`}>{e.name} · {fmtDate(e.eventDate)}</Link>
                    <button className="w-7 h-7 rounded-full flex items-center justify-center hover:bg-white/60" onClick={() => removeEvent(e.id)} data-testid={`contact-event-remove-${e.id}`}><X className="w-4 h-4" /></button>
                  </span>
                ))}
              </div>
            ) : <p className="text-[14px] text-[#64748B]">Not linked to any event yet.</p>}
          </Card>
          {c.notes && <Card title="Notes"><div className="flex gap-3"><StickyNote className="w-5 h-5 text-[#64748B] shrink-0 mt-0.5" /><p className="text-[15px] whitespace-pre-wrap">{c.notes}</p></div></Card>}
        </div>
        <div className="space-y-6">
          <Card title="Visiting card" testid="contact-card-images">
            {front || back ? (
              <div className="grid gap-3">
                {[front, back].filter(Boolean).map((src, i) => (
                  <button key={src} onClick={() => setPreview(src)} className="group relative rounded-[14px] overflow-hidden border border-[#E2E8F0] bg-[#F8FAFC]" data-testid={i ? "contact-card-back-image" : "contact-card-image"}>
                    <img src={src} alt="Visiting card" className="w-full aspect-[1.6/1] object-contain transition-transform group-hover:scale-[1.02]" />
                    <span className="absolute bottom-2 left-2 rounded-full bg-white/90 px-2.5 py-0.5 text-[11px] font-bold">{i ? "Back" : "Front"}</span>
                  </button>
                ))}
                <p className="text-[12px] text-[#64748B]">Click a card to view it full size.</p>
              </div>
            ) : <p className="text-[14px] text-[#64748B]">No card image saved.</p>}
          </Card>
          <div className="t-card p-5 [&>section]:mt-0"><ReminderSection contactId={id} /></div>
        </div>
      </div>
    </div>
  );
}
